/**
 * Opening the legal documents (`/privacy.html`, `/terms.html`).
 *
 * ── The failure this closes ────────────────────────────────────────────────
 *
 * **`target="_blank"` opens NOTHING inside Tauri.** The webview does not create
 * new windows, so a legal link with `_blank` is *unreachable* in the native
 * binary: the user taps and absolutely nothing happens. There is no error,
 * no log, and the link looks perfectly fine in the browser throughout
 * development.
 *
 * In bso this is a **silent noncompliance** with Google Play requirements,
 * which demand that the privacy policy be accessible **from the app** — and
 * the app Play reviews is precisely the native one.
 *
 * ── Why the webview is navigated and the system browser is not opened ───────
 *
 * `openUrl` from the `opener` plugin would open the system browser, but that
 * requires a public URL and therefore a **connection**. bso is an offline-first
 * app whose selling point is working without a network: a privacy policy that
 * can only be read with data would be the worst version of "accessible from the
 * app".
 *
 * The two documents are **bundled in the binary** (`frontendDist` includes
 * `apps/web/public/`), so navigating the webview to `/privacy.html` shows them
 * **without a network**, which is what the product promises. The page itself
 * carries a "← Back to the app".
 *
 * ── And why the `href` stays ────────────────────────────────────────────────
 *
 * The `<a>` keeps its real `href` and **does NOT have `target="_blank"`**: if
 * the JS failed, the anchor's default behavior still leads to the document on
 * both platforms. The degradation is to "works", not to "does nothing".
 */

import { isTauriRuntime } from "./platform";

/**
 * `window.open` features: without `opener` the new tab cannot touch the
 * app that opened it, and without `referrer` it leaks the source URL.
 *
 * They are declared as tokens and joined, instead of writing `"noopener,noreferrer"`
 * in one piece, because `lingui/no-unlocalized-strings` does not distinguish a UI
 * string from a browser flag: the comma-separated list matches none of its
 * exclusion patterns, while each standalone token does. This is preferable to an
 * `oxlint-disable`, which would turn off the rule for the whole line.
 */
const NEW_TAB_FEATURES = ["noopener", "noreferrer"].join(",");

/** Paths of the legal documents bundled with the app. */
export const LEGAL_PRIVACY_PATH = "/privacy.html";
export const LEGAL_TERMS_PATH = "/terms.html";

interface LegalWindow {
  open?: (url: string, target?: string, features?: string) => unknown;
  location?: { assign: (url: string) => void };
}

/**
 * Opens a legal document in the way that works on the current platform.
 * Returns how it was opened (useful for tests: the difference IS the fix).
 */
export function openLegalDocument(
  path: string,
  scope: LegalWindow = globalThis as LegalWindow,
  native: boolean = isTauriRuntime(),
): "navigate" | "new-tab" {
  if (native) {
    // Tauri: new tab impossible ⇒ navigate the webview to the bundled asset.
    scope.location?.assign(path);
    return "navigate";
  }
  const opened = scope.open?.(path, "_blank", NEW_TAB_FEATURES);
  if (opened) {
    return "new-tab";
  }
  // Pop-up blocker (or a `window.open` that returns null): the document must
  // not become unreachable, so navigation happens anyway.
  scope.location?.assign(path);
  return "navigate";
}

/**
 * Click handler for an `<a href="/privacy.html">`. `preventDefault` is
 * **synchronous** on purpose: doing it after an `await` arrives too late and
 * the browser will already have started the default navigation.
 */
export function handleLegalLinkClick(event: MouseEvent, path: string): void {
  // Respect user gestures to open elsewhere (ctrl/cmd/wheel) and let the
  // browser do its thing.
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) {
    return;
  }
  event.preventDefault();
  openLegalDocument(path);
}
